import { Injectable, inject } from '@angular/core';

import { HisBaseGlobalsService } from '../../core/service/base/his-base-globals.service';

import { DynamicContent, DynamicComponent, DynamicObjects, GUIComponent, GUIComponentField } from './dynamic-content';
import { HisDynamicFieldType } from './formfield';

@Injectable({providedIn: 'root'})
export class HisDynamicContentUtils {
    private hisBaseGlobalsService = inject(HisBaseGlobalsService);


    /* check if guiComponent is combined yesNoSelect and textfield */
    public checkForYesNoWithTextArea( guiComponent: GUIComponent, dynamicObjects: DynamicObjects[], dynamicApprovalResults?: any[]  ): boolean {
        if ( ( guiComponent.fields && guiComponent.fields.length === 2 ) || guiComponent.controlType === HisDynamicFieldType.DeclarationFieldset ) {
            let yesNoSelectField: GUIComponentField = guiComponent.fields.find(
                field => field.controlType === HisDynamicFieldType.YesNoSelect );
            let textField: GUIComponentField = guiComponent.fields.find(
                field => field.controlType === HisDynamicFieldType.Text || field.controlType === HisDynamicFieldType.TextArea );
            if ( yesNoSelectField && textField ) {
                guiComponent.controlType = HisDynamicFieldType.YesNoWithTextArea;
                // check if attributeName for YesNoSelectField and TextField contain number
                let numberRegex = RegExp( /\d+/g );
                if ( yesNoSelectField.attributeName.match( numberRegex ) && textField.attributeName.match( numberRegex ) ) {
                    // check if DD configuration for both is not hidden
                    let yesNoSelectDdEntry = this.hisBaseGlobalsService.getDd( yesNoSelectField.ddKey );
                    let textDdEntry = this.hisBaseGlobalsService.getDd( textField.ddKey );
                    // if yesNoSelect is hidden, the ddKey of the textField will be replaced to hide the complete component (DeclarationField)
                    if ( yesNoSelectDdEntry.hidden && !textDdEntry.hidden ) {
                        textField.ddKey = yesNoSelectField.ddKey;
                    }
                    if ( ( !yesNoSelectDdEntry || !yesNoSelectDdEntry.hidden ) && ( !textDdEntry || !textDdEntry.hidden )
                    || ( dynamicApprovalResults && dynamicApprovalResults.find( dynamicApprovalResult => dynamicApprovalResult.dynamicObject.dynamicClass.className === guiComponent.name ) ) ) {
                        if ( this.getDynamicObjectAttribute(
                            dynamicObjects, yesNoSelectField.objectUID, yesNoSelectField.attributeName, 'value' ) === '0' ) {
                            this.hisBaseGlobalsService.setRequiredToDdEntry( textField.ddKey, true );
                        }
                        return true;
                    }
                }
            }
        }
        return false;
    }

    /* get referenced dynamic object value */
    public getDynamicObjectAttribute( dynamicObjects: DynamicObjects[], objectUID: string,
        attributeName: string, attribute: string ): string {
        if ( dynamicObjects ) {
            let dynamicObject = dynamicObjects.find( dO => dO.objGuid === objectUID );
            if ( dynamicObject ) {
                let dynamicObjectValue: Record<string, any> = dynamicObject.values.find( dV => dV.attributeName === attributeName );
                if ( dynamicObjectValue ) {
                    return dynamicObjectValue[attribute];
                }
            }
        }
        return null;
    }

    /* function to check visbility of fields and fieldsets from DD-Keys */
    public atLeatOneTabChildFieldNotHidden(fieldsetChildren: GUIComponent[], contexts: string[]): boolean {
        let atLeastOnNotHidden = false;
        fieldsetChildren.forEach( ( fieldset: GUIComponent ) => {
            if ( fieldset.controlType === HisDynamicFieldType.YesNoWithTextArea || fieldset.controlType === HisDynamicFieldType.DeclarationFieldset ) {
                let yesNoSelectField: GUIComponentField = fieldset.fields.find(
                    ( field: GUIComponent ) => field.controlType === HisDynamicFieldType.YesNoSelect );
                if ( yesNoSelectField && !this.hisBaseGlobalsService.getDd( yesNoSelectField.ddKey, contexts )?.hidden ) {
                    atLeastOnNotHidden = true;
                }
            } else {
                fieldset.fields.forEach( ( field: GUIComponent ) => {
                    if ( !this.hisBaseGlobalsService.getDd( ( field.ddKey ? field.ddKey : field.name ), contexts )?.hidden ) {
                        atLeastOnNotHidden = true;
                    }
                } );
            }
        } );
        return atLeastOnNotHidden;
    }

    public isDynamicContentVisible(tabKey: string, dynamicComponent: DynamicComponent, contexts?: string[]): boolean {
        let personTabChildren: GUIComponent = dynamicComponent.guiComponent.children.find(
            (tabChild: GUIComponent) => tabChild.name === tabKey);
        return this.atLeatOneTabChildFieldNotHidden(personTabChildren ? personTabChildren.children : [], (contexts ? contexts : dynamicComponent.conditions.contexts));
    }

    public isStaticContentVisible(dDKeys: string[], dynamicComponent: DynamicComponent, contexts?: string[]): boolean {
        let isStaticContentVisible: boolean = false;
        dDKeys.forEach((key: string) => {
            let dDEntry = this.hisBaseGlobalsService.getDd(key, (contexts ? contexts : dynamicComponent?.conditions.contexts));
            if (!dDEntry || dDEntry && !dDEntry.hidden) {
                isStaticContentVisible = true;
            }
        });
        return isStaticContentVisible;
    }

    /* merge dynamicContent of two objects */
    public getCombinedDynamicContent(_dynamicComponentObjectOne: DynamicContent, _dynamicComponentObjectTwo: DynamicContent): DynamicContent {
        if (_dynamicComponentObjectOne._dynamicComponent.guiComponent.visibleAttributes) {
            let combinedDynamicContent = new DynamicContent();
            combinedDynamicContent._dynamicComponent = JSON.parse(JSON.stringify(_dynamicComponentObjectOne._dynamicComponent));
            _dynamicComponentObjectTwo._dynamicComponent.guiComponent.children.forEach(
                (tabChildTwo: GUIComponent) => {
                    let sameTabDefinition = combinedDynamicContent._dynamicComponent.guiComponent.children.find((tabChildOne: GUIComponent) => tabChildOne.name === tabChildTwo.name);
                    if (sameTabDefinition) {
                        sameTabDefinition.children.push(...tabChildTwo.children);
                    } else {
                        combinedDynamicContent._dynamicComponent.guiComponent.children.push(tabChildTwo);
                    }
                }
            );
            // combine dynamicObjects
            _dynamicComponentObjectTwo._dynamicComponent.dynamicObjects.forEach(
                (dynamicObject: DynamicObjects) => {
                    combinedDynamicContent._dynamicComponent.dynamicObjects.push(dynamicObject);
                }
            );
            // combine conditions.contexts
            this.joinConditionsContexts( _dynamicComponentObjectTwo, combinedDynamicContent );
            
            return combinedDynamicContent;
        }
        // combine conditions.contexts
        this.joinConditionsContexts( _dynamicComponentObjectOne, _dynamicComponentObjectTwo );
        
        return _dynamicComponentObjectTwo;
    }

    private joinConditionsContexts( sourceDynamicComponentObject: DynamicContent, joinToDynamicComponentObject: DynamicContent ): void {
        if ( sourceDynamicComponentObject._dynamicComponent.conditions ) {
            sourceDynamicComponentObject._dynamicComponent.conditions.contexts.forEach(
                ( context: string ) => 
                    joinToDynamicComponentObject._dynamicComponent.conditions.contexts.indexOf( context ) === -1
                        ? joinToDynamicComponentObject._dynamicComponent.conditions.contexts.push( context ) 
                        : null
            );
        }
    }
    
}
